import type { ContextGeoSubject } from '@/shared/types/geography.ts';
import { trackEvent } from '@/entities/sogu';
import { useScreenSize } from '@experience/shared';
import { tourMapFilterDataToSogu, useTourFiltersStore, type TTourFiltersState } from '@/entities/filter/store/tour';
import { removeNullableProperties } from '@/shared/lib/removeNullableProperties.ts';
import type { ListingFilterOpenParams } from '@/shared/api/sogu';
import { LocationType } from '@/entities/location';
import { type MaybeRefOrGetter } from 'vue';
import { useFiltersDateRange } from '@/features/filter/dates/composables/useFiltersDateRange.ts';

export type ToursSoguFilterTypes =
  | 'price'
  | 'all'
  | 'duration'
  | 'date'
  | 'date_mobile'
  | 'tourFormat'
  | 'activity_type';

export function useToursFilterBlockSogu(location?: MaybeRefOrGetter<LocationType>) {
  const { isMobile } = useScreenSize();
  const filterStore = useTourFiltersStore();
  const { fastDateRangeRef, lastAppliedFastDateRangeRef } = useFiltersDateRange();

  const isCountryTour = computed(() => toValue(location) === LocationType.COUNTRY);

  const soguFilterOpenEvent = (
    soguFields: ContextGeoSubject,
    filterType: ToursSoguFilterTypes,
    isForced: boolean = false,
  ) => {
    if (filterType === 'date' && isMobile.value) {
      filterType = 'date_mobile';
    }

    const eventName: ListingFilterOpenParams['event_name'] = isCountryTour.value
      ? 'country-tours-listing:filter.open'
      : 'listing:filter.open';

    return trackEvent(eventName, buildSoguEventParams(soguFields, filterType, isForced));
  };

  const soguFilterApplyEvent = (
    soguFields: ContextGeoSubject,
    filterType: ToursSoguFilterTypes,
    isForced: boolean = false,
    customFilterData?: Partial<TTourFiltersState>,
  ) => {
    if (filterType === 'date' && isMobile.value) {
      filterType = 'date_mobile';
    }

    const dataToMap = customFilterData || filterStore.searchState;

    const { price_start, price_end, ...mappedParams } = removeNullableProperties(tourMapFilterDataToSogu(dataToMap));

    const params: Record<string, unknown> = {
      ...mappedParams,
    };

    if (price_start) {
      params.start_price = price_start;
    }

    if (price_end) {
      params.end_price = price_end;
    }

    if (fastDateRangeRef.value) {
      params.fast_date_range = fastDateRangeRef.value;
      lastAppliedFastDateRangeRef.value = fastDateRangeRef.value;
    } else {
      lastAppliedFastDateRangeRef.value = null;
    }

    const eventName = isCountryTour.value ? 'country-tours-listing:filter.apply' : 'listing:filter:tours.apply';

    return trackEvent(eventName, buildSoguEventParams(soguFields, filterType, isForced, params));
  };

  const soguFilterResetEvent = (
    soguFields: ContextGeoSubject,
    filterType: ToursSoguFilterTypes,
    isForced: boolean = false,
  ) => {
    const params: Record<string, unknown> = removeNullableProperties(tourMapFilterDataToSogu(filterStore.searchState));

    if (lastAppliedFastDateRangeRef.value) {
      params.fast_date_range = lastAppliedFastDateRangeRef.value;
    }

    const eventName = isCountryTour.value ? 'country-tours-listing:filter.reset' : 'listing:filter:tours.reset';

    return trackEvent(eventName, buildSoguEventParams(soguFields, filterType, isForced, params));
  };

  const buildSoguEventParams = (
    soguFields: ContextGeoSubject,
    filterType: ToursSoguFilterTypes,
    isForced: boolean = false,
    additionalParams: Record<string, unknown> = {},
  ) => {
    return {
      ...soguFields.soguStamp,
      filter_type: filterType,
      filter_window_type: isForced ? 'auto_listing_open_popup' : 'manual_listing_open_popup',
      ...additionalParams,
    };
  };

  return {
    soguFilterOpenEvent,
    soguFilterApplyEvent,
    soguFilterResetEvent,
  };
}
